import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import SiteVisitScheduler from "../../components/crm/SiteVisitScheduler";
import AssignmentCard from "../../components/crm/AssignmentCard";
import TasksCard from "../../components/crm/TasksCard";
import ActivityTimeline from "../../components/crm/ActivityTimeline";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Repeat, ArrowLeft, User, Mail, Phone, MapPin, Calendar, MessageSquare, Calculator, FolderOpen, Send } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new","contacted","qualified","site_visit_scheduled","proposal_sent","won","lost","nurture"];

export default function LeadDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [visits, setVisits] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState(null);
  const [replySending, setReplySending] = useState(false);
  const [openReplyId, setOpenReplyId] = useState(null);
  // R1e — SMS consent + suppression state, read-only badge next to phone.
  const [smsState, setSmsState] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const d = (await api.get(`/leads/${id}`)).data;
      setData(d);
      // R1d.8 — Mark all inbound interactions as read when the detail
      // page loads (LeadDetail is the surface where the user views them).
      // Fire-and-forget: don't block the render on the mark-read call.
      api.post(`/leads/${id}/interactions/mark-all-read`).catch(() => {});
      // Load related side-lists in parallel; server-scoped by tenant.
      const [v, e, doc] = await Promise.all([
        api.get("/site-visits").then(r => r.data).catch(() => []),
        api.get("/estimates").then(r => r.data).catch(() => []),
        api.get("/documents").then(r => r.data).catch(() => []),
      ]);
      const oppId = d.lead?.converted_opportunity_id;
      setVisits(v.filter(x => x.lead_id === id || (oppId && x.opportunity_id === oppId)));
      setEstimates(e.filter(x => oppId && x.opportunity_id === oppId));
      setDocuments(doc.filter(x => x.linked_lead_id === id));
      // R1e — fire-and-forget: pull the current SMS state for this lead.
      api.get(`/leads/${id}/sms-state`).then((r) => setSmsState(r.data)).catch(() => setSmsState(null));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // R1d.5 — Auto-open reply composer when arriving from digest with ?reply=1
  useEffect(() => {
    if (!data || replyOpen || replyDraft) return;
    if (params.get("reply") === "1") {
      openReplyComposer(params.get("template") || "digest");
    }
  }, [data, params]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openReplyComposer(template = "digest") {
    try {
      const r = await api.post(`/leads/${id}/reply-template`, null, { params: { template } });
      setReplyDraft({
        subject: r.data.subject,
        body: r.data.body,
        to: r.data.lead?.email || "",
        ref_slug: r.data.ref_slug,
        template,
      });
      setReplyOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function sendReply() {
    if (!replyDraft?.body || !replyDraft?.subject) return toast.error("Subject and body required.");
    setReplySending(true);
    try {
      const r = await api.post(`/leads/${id}/reply-send`, replyDraft);
      toast.success(r.data.label || "Reply queued");
      setReplyOpen(false);
      setReplyDraft(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReplySending(false);
    }
  }

  const setStatus = async (status) => {
    try { await api.patch(`/leads/${id}`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async () => {
    if (!window.confirm("Convert this lead to an Opportunity?")) return;
    try { await api.post(`/leads/${id}/convert`); toast.success("Converted"); navigate("/crm/opportunities"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (err) return <AppShell title="Lead"><div className="text-red-700 text-sm">{err}</div></AppShell>;
  if (!data) return <AppShell title="Lead"><div className="font-mono text-xs text-erf-dark/60">Loading…</div></AppShell>;

  const l = data.lead;
  const emailReplies = (data.interactions || []).filter((e) => e.kind === "email_reply");
  const auditInteractions = (data.timeline || []).filter(e =>
    ["log_call", "log_email", "log_sms", "message", "note"].includes(e.action)
  );

  return (
    <AppShell
      title={`${l.first_name} ${l.last_name}`}
      subtitle={`Lead ${l.lead_number || l.id.slice(0,8)} · ${l.sector}`}
      actions={<Link to="/crm/leads"><Button variant="outline" className="border-erf-borderCream" data-testid="lead-detail-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>}
    >
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview" data-testid="lead-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="interactions" data-testid="lead-tab-interactions">Interactions</TabsTrigger>
              <TabsTrigger value="followup" data-testid="lead-tab-followup">Follow-up</TabsTrigger>
              <TabsTrigger value="visits" data-testid="lead-tab-visits">Site Visits</TabsTrigger>
              <TabsTrigger value="estimates" data-testid="lead-tab-estimates">Estimates</TabsTrigger>
              <TabsTrigger value="files" data-testid="lead-tab-files">Files</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="lead-tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="notes" data-testid="lead-tab-notes">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5 grid gap-3 md:grid-cols-2">
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Company</div><div className="font-nav">{l.company || "—"}</div></div>
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Source</div><div className="mono text-xs">{l.source}</div></div>
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Project type</div><div className="font-nav">{l.project_type || "—"}</div></div>
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Budget</div><div className="font-nav">{l.budget_range || "—"}</div></div>
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Timeframe</div><div className="font-nav">{l.timeframe || "—"}</div></div>
                <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Preferred contact</div><div className="font-nav">{l.preferred_contact || "—"}</div></div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="interactions" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3 text-erf-dark/70"><MessageSquare className="w-4 h-4" /> Calls / emails / SMS logged against this lead</div>
                {emailReplies.length === 0 && auditInteractions.length === 0 ? (
                  <div className="text-xs font-mono text-erf-dark/50 py-4">No interactions logged. Use the Owner Command Center outbox and note API to record calls, emails, and SMS.</div>
                ) : (
                  <div className="space-y-2">
                    {emailReplies.map((e) => {
                      const isOpen = openReplyId === e.id;
                      const badgeStyle = e.sendgrid_state === "pending_provider"
                        ? "bg-emerald-50 text-emerald-900 border-emerald-200"
                        : "bg-zinc-100 text-zinc-800 border-zinc-300";
                      const badgeLabel = e.sendgrid_state === "pending_provider"
                        ? "Queued (SendGrid)" : "Outbox only";
                      return (
                        <div
                          key={e.id}
                          className="border border-erf-borderCream rounded-md bg-white cursor-pointer hover:bg-erf-creamSecondary/30"
                          onClick={() => setOpenReplyId(isOpen ? null : e.id)}
                          data-testid={`lead-email-reply-${e.id}`}
                        >
                          <div className="flex items-start justify-between gap-3 p-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Mail className="w-3.5 h-3.5 text-erf-goldHover" />
                                <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
                                  email · outbound
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-nav font-semibold uppercase tracking-wider border ${badgeStyle}`}>
                                  {badgeLabel}
                                </span>
                                {e.ref_slug && (
                                  <span className="text-[10px] font-mono text-erf-dark/50">ref: {e.ref_slug}</span>
                                )}
                              </div>
                              <div className="font-nav font-semibold text-erf-dark text-sm mt-1">{e.subject}</div>
                              {!isOpen && (
                                <div className="text-xs font-nav text-erf-dark/70 mt-1 line-clamp-2">
                                  {e.body_preview}{(e.body || e.body_preview || "").length >= 280 ? "…" : ""}
                                </div>
                              )}
                            </div>
                            <div className="text-[10px] font-mono text-erf-dark/50 whitespace-nowrap">
                              {(e.created_at || "").replace("T", " ").slice(0, 19)}
                            </div>
                          </div>
                          {isOpen && (
                            <div className="border-t border-erf-borderCream/60 px-3 py-3 text-sm font-nav whitespace-pre-wrap text-erf-dark/85 bg-erf-creamSecondary/30" data-testid={`lead-email-reply-body-${e.id}`}>
                              {e.body || e.body_preview}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {auditInteractions.map((e) => (
                      <div key={e.id || e.hash_chain_index} className="border-b border-erf-borderCream/60 py-2 text-sm" data-testid={`lead-interaction-${e.hash_chain_index}`}>
                        <span className="mono text-[11px] text-erf-dark/70">#{e.hash_chain_index} · {e.action} · {e.actor_role}</span>
                        <div className="mono text-[10px] text-erf-dark/50">{(e.ts||"").replace("T"," ").slice(0,19)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="followup" className="mt-4 space-y-4">
              <AssignmentCard entity="lead" id={id} record={l} onSaved={load} />
              <div className="grid gap-4 lg:grid-cols-2">
                <TasksCard entity="lead" id={id} />
                <ActivityTimeline entity="lead" id={id} />
              </div>
            </TabsContent>

            <TabsContent value="visits" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-erf-dark/70"><Calendar className="w-4 h-4" /> Site visits linked to this lead</div>
                  <Link to="/crm/site-visits" className="text-[11px] font-nav underline inline-flex min-h-[44px] items-center" data-testid="lead-visit-calendar-link">Calendar</Link>
                </div>
                <SiteVisitScheduler leadId={id} opportunityId={l.converted_opportunity_id} visits={visits} onChange={load} />
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="estimates" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3 text-erf-dark/70"><Calculator className="w-4 h-4" /> Estimates on the converted opportunity</div>
                {estimates.length === 0 ? (
                  <div className="text-xs font-mono text-erf-dark/50 py-4">{l.converted_opportunity_id ? "No estimates yet." : "Convert this lead to an Opportunity before creating estimates."}</div>
                ) : estimates.map(e => (
                  <Link key={e.id} to="/crm/estimates" className="block border-b border-erf-borderCream/60 py-2 text-sm hover:bg-erf-creamSecondary" data-testid={`lead-estimate-${e.id}`}>
                    <div className="font-nav">{e.name} — <span className="mono">${Number(e.total).toLocaleString()}</span></div>
                    <div className="mono text-[11px] text-erf-dark/60">v{e.version} · {e.status}</div>
                  </Link>
                ))}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3 text-erf-dark/70"><FolderOpen className="w-4 h-4" /> Documents attached to this lead</div>
                {documents.length === 0 ? (
                  <div className="text-xs font-mono text-erf-dark/50 py-4">No documents yet. Upload via <Link to="/admin/documents" className="text-erf-gold hover:underline">Documents Vault</Link> and link back to this lead.</div>
                ) : documents.map(d => (
                  <div key={d.id} className="border-b border-erf-borderCream/60 py-2 text-sm" data-testid={`lead-file-${d.id}`}>
                    <div className="font-nav">{d.file_name}</div>
                    <div className="mono text-[11px] text-erf-dark/60">{d.classification} · {Math.round((d.size_bytes||0)/1024)}kB</div>
                  </div>
                ))}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <div className="bg-white border border-erf-borderCream rounded-md p-2 max-h-[60vh] overflow-y-auto">
                {(data.timeline || []).length === 0 ? (
                  <div className="p-4 font-mono text-xs text-erf-dark/50">No audit entries yet.</div>
                ) : data.timeline.map((e) => (
                  <div key={e.id} className="border-b border-erf-borderCream/60 py-2 px-2" data-testid={`lead-timeline-${e.hash_chain_index}`}>
                    <div className="mono text-[11px] text-erf-dark/80">#{e.hash_chain_index} · {e.action} · {e.actor_role}</div>
                    <div className="mono text-[10px] text-erf-dark/50">{(e.ts||"").replace("T"," ").slice(0,19)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <Card className="border-erf-borderCream"><CardContent className="p-5"><pre className="text-sm font-nav whitespace-pre-wrap">{l.notes || "No notes."}</pre></CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-4">
          <Card className="border-erf-borderCream">
            <CardContent className="p-5 space-y-3">
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60 mb-1">Status</div>
                <Select value={l.status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="lead-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="border-t border-erf-borderCream pt-3 space-y-2 text-sm font-nav">
                <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-erf-gold" /> {l.first_name} {l.last_name}</div>
                {l.email && <div className="flex items-center gap-2 font-mono text-xs"><Mail className="w-3.5 h-3.5 text-erf-gold" /> {l.email}</div>}
                {l.phone && (
                  <div className="flex items-center gap-2 font-mono text-xs flex-wrap" data-testid="lead-phone-row">
                    <Phone className="w-3.5 h-3.5 text-erf-gold" /> {l.phone}
                    {/* R1e — SMS consent / suppression badge, read-only. */}
                    {smsState && smsState.phone_present && (() => {
                      if (smsState.suppressed) {
                        return (
                          <span
                            data-testid="lead-sms-badge"
                            data-sms-state="suppressed"
                            title="This number replied STOP to a prior message. Outbound SMS is blocked."
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-nav font-semibold uppercase tracking-wider border bg-red-50 text-red-800 border-red-200"
                          >
                            SMS: opted out via STOP
                          </span>
                        );
                      }
                      if (smsState.consent && smsState.consent.status === "granted") {
                        return (
                          <span
                            data-testid="lead-sms-badge"
                            data-sms-state="consented"
                            title={`Web opt-in via ${smsState.consent.source_form || "form"} — ${smsState.consent.granted_at || ""}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-nav font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-900 border-emerald-200"
                          >
                            SMS: consented {smsState.consent.policy_version || "v1.0"}
                          </span>
                        );
                      }
                      return (
                        <span
                          data-testid="lead-sms-badge"
                          data-sms-state="no-consent"
                          title="No SMS consent on file — do not send automated SMS to this number."
                          className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-nav font-semibold uppercase tracking-wider border bg-zinc-100 text-zinc-800 border-zinc-300"
                        >
                          SMS: no consent on file
                        </span>
                      );
                    })()}
                  </div>
                )}
                {l.address_line1 && <div className="flex items-start gap-2 text-xs"><MapPin className="w-3.5 h-3.5 text-erf-gold mt-0.5" /> <span>{l.address_line1}{l.city && `, ${l.city}`} {l.state} {l.zip}</span></div>}
              </div>
              <div className="border-t border-erf-borderCream pt-3 space-y-2">
                {!l.converted_opportunity_id && (
                  <Button onClick={convert} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="lead-detail-convert">
                    <Repeat className="w-4 h-4 mr-1" /> Convert to Opportunity
                  </Button>
                )}
                {l.converted_opportunity_id && (
                  <Link to="/crm/opportunities" data-testid="lead-detail-goto-opp">
                    <Button variant="outline" className="w-full">View Opportunity →</Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* R1d.5 — Reply composer (auto-opens from digest ?reply=1) */}
      <Dialog open={replyOpen} onOpenChange={(o) => { if (!o) { setReplyOpen(false); setReplyDraft(null); } }}>
        <DialogContent className="max-w-2xl bg-erf-cream" data-testid="lead-reply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-erf-goldHover" />
              Reply to {l?.name || "lead"}
              {replyDraft?.ref_slug && (
                <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">
                  from "{replyDraft.ref_slug}"
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {replyDraft && (
            <div className="grid gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">To</Label>
                <Input value={replyDraft.to} onChange={(e) => setReplyDraft({ ...replyDraft, to: e.target.value })} data-testid="reply-to" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Subject</Label>
                <Input value={replyDraft.subject} onChange={(e) => setReplyDraft({ ...replyDraft, subject: e.target.value })} data-testid="reply-subject" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Message</Label>
                <textarea
                  rows={12} value={replyDraft.body}
                  onChange={(e) => setReplyDraft({ ...replyDraft, body: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav"
                  data-testid="reply-body"
                />
              </div>
              <div className="text-[11px] font-mono text-erf-dark/60">
                Send will queue to the notifications outbox. When SendGrid is connected, the outbox drains through the send worker.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReplyOpen(false); setReplyDraft(null); }} data-testid="reply-cancel">
                  Cancel
                </Button>
                <Button onClick={sendReply} disabled={replySending} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="reply-send">
                  <Send className="w-3.5 h-3.5 mr-1" /> {replySending ? "Queuing…" : "Send reply"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
